<template>
  <div>
    <v-skeleton-loader
      :loading="loading"
      type="article, list-item-two-line, actions"
    >
      <v-tabs centered>
        <v-tab>{{ $t("restaurant") }}</v-tab>
        <v-tab>{{ $t("account") }}</v-tab>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-container>
                <v-form ref="form" v-model="onlineOrderingValid">
                  <v-card-title class="headline pl-0">
                    <span
                      :class="
                        $vuetify.breakpoint.xs
                          ? 'black--text ellipsis'
                          : 'black--text'
                      "
                    >
                      {{ $t("onlineOrdering") }}
                    </span>
                    <v-btn
                      :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                      color="success"
                      text
                      rounded
                      :disabled="!onlineOrderingValid"
                      @click="saveOnlineOrdering()"
                    >
                      <span v-if="$vuetify.breakpoint.xs">
                        <v-icon>mdi-check-circle</v-icon>
                      </span>
                      <span v-else>
                        {{ $t("save") }}
                      </span>
                    </v-btn>
                  </v-card-title>
                  <v-row>
                    <v-row class="pl-3" style="width: 100%">
                      <v-col cols="auto">
                        <v-switch
                          class="mt-0 pt-0 d-inline"
                          v-model="restaurant.isOpen"
                          :disabled="restaurant.automaticIsOpen"
                          :label="
                            restaurant.isOpen
                              ? $t('orderingEnabled')
                              : $t('orderingDisabled')
                          "
                          :color="
                            restaurant.automaticIsOpen ? 'grey' : 'primary'
                          "
                          hide-details
                        ></v-switch>
                      </v-col>
                      <v-col cols="auto">
                        <v-checkbox
                          class="mt-0 pt-0 d-inline float-left"
                          v-model="restaurant.automaticIsOpen"
                          :label="$t('automaticIsOpen')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="auto">
                        <v-checkbox
                          class="mt-0 pt-0 d-inline float-left"
                          v-model="restaurant.allowPreorderingWhenClosed"
                          @change="
                            restaurant.allowPreorderingWhenClosed
                              ? (restaurant.orderScheduling = true)
                              : null
                          "
                          :label="$t('allowPreorderingWhenClosed')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="auto">
                        <v-checkbox
                          class="mt-0 pt-0 d-inline float-left"
                          v-model="restaurant.orderScheduling"
                          :disabled="restaurant.allowPreorderingWhenClosed"
                          :label="$t('orderScheduling')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                    <v-col cols="12" md="4">
                      <v-menu
                        ref="menu1"
                        v-model="timePickerMenuStatus1"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="timePickerMenu1"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="restaurant.dailyMenuOrderingTime.from"
                            :label="$t('dailyMenuOrderingStart')"
                            append-icon="mdi-clock-time-four-outline"
                            readonly
                            filled
                            rounded
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-if="timePickerMenuStatus1"
                          full-width
                          @click:minute="$refs.menu1.save(timePickerMenu1)"
                          v-model="restaurant.dailyMenuOrderingTime.from"
                          format="24hr"
                        ></v-time-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-menu
                        ref="menu2"
                        v-model="timePickerMenuStatus2"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="timePickerMenu2"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="restaurant.dailyMenuOrderingTime.to"
                            :label="$t('dailyMenuOrderingEnd')"
                            append-icon="mdi-clock-time-four-outline"
                            readonly
                            filled
                            rounded
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-if="timePickerMenuStatus2"
                          full-width
                          @click:minute="$refs.menu2.save(timePickerMenu2)"
                          v-model="restaurant.dailyMenuOrderingTime.to"
                          format="24hr"
                        ></v-time-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="restaurant.minimalOrderValue"
                        :label="$t('minimalOrderValue')"
                        :rules="[rules.required, rules.minimalOrderValue]"
                        type="number"
                        rounded
                        filled
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      v-if="restaurant.paymentOptions.onlinePayments.status"
                    >
                      <v-text-field
                        v-model="
                          restaurant.paymentOptions.onlinePayments.returnUrl
                        "
                        :label="$t('onlinePaymentReturnUrl')"
                        :rules="[rules.required, rules.url]"
                        :hint="$t('onlinePaymentReturnUrlHint')"
                        persistent-hint
                        rounded
                        filled
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="restaurant.ordersNotificationEmail"
                        :label="$t('ordersNotificationEmail')"
                        :rules="[rules.required, rules.commaSeparatedEmails]"
                        :hint="$t('ordersNotificationEmailHint')"
                        persistent-hint
                        rounded
                        filled
                      ></v-text-field>
                    </v-col>
                    <v-row class="pl-3">
                      <v-col cols="auto">
                        <span class="mt-0 pt-0 mr-4 d-inline float-left"
                          >{{ $t("deliveryOptions") }}:</span
                        >
                        <v-checkbox
                          class="mt-0 pt-0 mr-4 d-inline float-left"
                          v-model="restaurant.deliveryOptions.takeaway.status"
                          :disabled="
                            !restaurant.deliveryOptions.delivery.status &&
                            !restaurant.deliveryOptions.inplace.status
                          "
                          :label="$t('takeaway')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                        <v-checkbox
                          class="mt-0 pt-0 mr-4 d-inline float-left"
                          v-model="restaurant.deliveryOptions.delivery.status"
                          :disabled="
                            !restaurant.deliveryOptions.takeaway.status &&
                            !restaurant.deliveryOptions.inplace.status
                          "
                          :label="$t('delivery')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                        <v-checkbox
                          class="mt-0 pt-0 d-inline float-left"
                          v-model="restaurant.deliveryOptions.inplace.status"
                          :disabled="
                            !restaurant.deliveryOptions.takeaway.status &&
                            !restaurant.deliveryOptions.delivery.status
                          "
                          :label="$t('inplace')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>

                      <v-col cols="auto">
                        <span class="mt-0 pt-0 mr-4 d-inline float-left"
                          >{{ $t("paymentOptions") }}:</span
                        >
                        <v-checkbox
                          class="mt-0 pt-0 mr-4 d-inline float-left"
                          v-model="
                            restaurant.paymentOptions.cashOnDelivery.status
                          "
                          disabled
                          :label="$t('cod')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                        <v-checkbox
                          class="mt-0 pt-0 d-inline float-left"
                          v-model="
                            restaurant.paymentOptions.onlinePayments.status
                          "
                          disabled
                          :label="$t('onlinePayments')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>

                      <v-col cols="auto">
                        <span class="mt-0 pt-0 mr-4 d-inline float-left"
                          >{{ $t("cashOnDeliveryAllowedTypes") }}:</span
                        >
                        <v-checkbox
                          class="mt-0 pt-0 mr-4 d-inline float-left"
                          v-model="
                            restaurant.paymentOptions.cashOnDelivery
                              .allowedTypes.cash
                          "
                          :disabled="
                            !restaurant.paymentOptions.cashOnDelivery
                              .allowedTypes.card
                          "
                          :label="$t('cash')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                        <v-checkbox
                          class="mt-0 pt-0 d-inline float-left"
                          v-model="
                            restaurant.paymentOptions.cashOnDelivery
                              .allowedTypes.card
                          "
                          :disabled="
                            !restaurant.paymentOptions.cashOnDelivery
                              .allowedTypes.cash
                          "
                          :label="$t('card')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>

                      <v-col cols="12">
                        <v-btn
                          color="primary"
                          class="mr-2"
                          depressed
                          rounded
                          small
                          @click="connectStripe()"
                          :disabled="
                            restaurant.paymentOptions.onlinePayments.status
                          "
                          :loading="connectStripeLoading"
                          >{{
                            $vuetify.breakpoint.xs
                              ? $t("acceptOnlinePayments")
                              : $t("acceptOnlinePaymentsWithStripe")
                          }}</v-btn
                        >
                        <v-dialog v-model="portosDialog" width="500" persistent>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              color="primary"
                              depressed
                              rounded
                              small
                              v-on="on"
                              :class="$vuetify.breakpoint.xs ? 'mt-2' : ''"
                            >
                              {{ $t("connectWithPortosCashRegister") }}
                            </v-btn>
                          </template>

                          <v-card>
                            <v-card-title class="headline mb-2">
                              {{ $t("connectWithPortosCashRegister") }}
                            </v-card-title>
                            <v-card-subtitle>{{
                              $t("easilyIntegrateWithCashRegisterSystem")
                            }}</v-card-subtitle>

                            <v-card-text class="pa-4 pt-0">
                              <v-col cols="12">
                                <v-text-field
                                  :label="$t('unprocessedOrdersUrl')"
                                  value="https://api.piknik.cc/portos/unprocessedorders"
                                  rounded
                                  filled
                                  hide-details
                                  readonly
                                >
                                </v-text-field>
                              </v-col>
                              <v-col cols="12">
                                <v-text-field
                                  :label="$t('processOrderUrl')"
                                  value="https://api.piknik.cc/portos/processorder"
                                  rounded
                                  filled
                                  hide-details
                                  readonly
                                >
                                </v-text-field>
                              </v-col>
                              <v-col cols="12">
                                <v-textarea
                                  v-model="portosToken"
                                  :label="$t('apiKey')"
                                  :hint="
                                    portosToken
                                      ? $t('portosApiKeyHint2')
                                      : $t('portosApiKeyHint1')
                                  "
                                  :placeholder="
                                    restaurant.portosToken || $t('noApiKey')
                                  "
                                  rows="1"
                                  rounded
                                  filled
                                  auto-grow
                                  readonly
                                  persistent-hint
                                >
                                </v-textarea>
                              </v-col>
                            </v-card-text>

                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn
                                color="error"
                                text
                                rounded
                                :loading="generatePortosTokenLoading"
                                :disabled="portosToken ? true : false"
                                @click="generatePortosToken()"
                                >{{ $t("generatePortosApiKey") }}</v-btn
                              >
                              <v-btn
                                color="primary"
                                text
                                rounded
                                @click="
                                  portosDialog = false;
                                  portosToken = null;
                                "
                                >{{ $t("close") }}</v-btn
                              >
                            </v-card-actions>
                          </v-card>
                        </v-dialog>
                      </v-col>
                    </v-row>
                  </v-row>
                </v-form>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("names") }}
                  </span>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="restaurant.names.name"
                      :label="$t('name')"
                      disabled
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="restaurant.names.urlName"
                      :label="$t('urlName')"
                      disabled
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="restaurant.names.voiceName"
                      :label="$t('voiceName')"
                      disabled
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("businessDetails") }}
                  </span>
                  <v-btn
                    :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                    color="success"
                    text
                    rounded
                    @click="saveBusinessDetails()"
                  >
                    <span v-if="$vuetify.breakpoint.xs">
                      <v-icon>mdi-check-circle</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("save") }}
                    </span>
                  </v-btn>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.legalName"
                      :label="$t('legalName')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.businessId"
                      :label="$t('businessId')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.taxId"
                      :label="$t('taxId')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.vat"
                      :label="$t('vat')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.address.street"
                      :label="$t('street')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.address.city"
                      :label="$t('city')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.address.zip"
                      type="number"
                      :label="$t('zip')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.businessDetails.address.state"
                      :label="$t('state')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("information") }}
                  </span>
                  <v-btn
                    :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                    color="success"
                    text
                    rounded
                    @click="saveRestaurantInfo()"
                  >
                    <span v-if="$vuetify.breakpoint.xs">
                      <v-icon>mdi-check-circle</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("save") }}
                    </span>
                  </v-btn>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="restaurant.info.contact.website"
                      :label="$t('website')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="restaurant.info.contact.email"
                      :label="$t('email')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="restaurant.info.contact.mobile"
                      :label="$t('mobile')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.info.contact.address.street"
                      :label="$t('street')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.info.contact.address.city"
                      :label="$t('city')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.info.contact.address.zip"
                      type="number"
                      :label="$t('zip')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="restaurant.info.contact.address.state"
                      :label="$t('state')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="restaurant.info.description"
                      :label="$t('description')"
                      auto-grow
                      rounded
                      filled
                      hide-details
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      :label="$t('openingHours')"
                      :hint="$t('openingHoursHint')"
                      prepend-inner-icon="mdi-eye"
                      readonly
                      rounded
                      filled
                      persistent-hint
                      @click:prepend-inner="dialog = true"
                    >
                      <v-icon style="vertical-align: middle">mdi-eye</v-icon>
                      <template v-slot:label>
                        {{ $t("click") }}
                        <v-icon style="vertical-align: middle" small
                          >mdi-eye</v-icon
                        >&#160;{{ $t("toShowOpeningHours") }}
                      </template>
                    </v-text-field>
                    <v-dialog v-model="dialog" max-width="450" persistent>
                      <v-card>
                        <v-card-title class="headline">{{
                          $t("openingHours")
                        }}</v-card-title>
                        <v-card-text>
                          <v-container>
                            <v-row>
                              <v-col cols="12">
                                <v-data-table
                                  :headers="headers"
                                  :header-props="{ sortByText: $t('sortBy') }"
                                  :items="restaurant.info.openingHours"
                                  hide-default-footer
                                >
                                  <template v-slot:item.day="{ item }">
                                    <v-chip v-if="item.day === 'monday'">{{
                                      $t("monday")
                                    }}</v-chip>
                                    <v-chip v-if="item.day === 'tuesday'">{{
                                      $t("tuesday")
                                    }}</v-chip>
                                    <v-chip v-if="item.day === 'wednesday'">{{
                                      $t("wednesday")
                                    }}</v-chip>
                                    <v-chip v-if="item.day === 'thursday'">{{
                                      $t("thursday")
                                    }}</v-chip>
                                    <v-chip v-if="item.day === 'friday'">{{
                                      $t("friday")
                                    }}</v-chip>
                                    <v-chip v-if="item.day === 'saturday'">{{
                                      $t("saturday")
                                    }}</v-chip>
                                    <v-chip v-if="item.day === 'sunday'">{{
                                      $t("sunday")
                                    }}</v-chip>
                                  </template>
                                  <template v-slot:item.from="props">
                                    <v-edit-dialog
                                      :return-value.sync="props.item.from"
                                      large
                                      :cancel-text="$t('close')"
                                      :save-text="$t('save')"
                                    >
                                      <div>{{ props.item.from }}</div>
                                      <!-- <template v-slot:input>
                                      <div class="mt-4 title">Update time</div>
                                    </template> -->
                                      <template v-slot:input>
                                        <v-select
                                          menu-props="auto"
                                          :items="getOpeningHoursTimes()"
                                          style="margin-top: 16px"
                                          v-model="props.item.from"
                                          label="10:00"
                                          single-line
                                          filled
                                          rounded
                                          hide-details
                                        ></v-select>
                                      </template>
                                    </v-edit-dialog>
                                  </template>
                                  <template v-slot:item.to="props">
                                    <v-edit-dialog
                                      :return-value.sync="props.item.to"
                                      large
                                      :cancel-text="$t('close')"
                                      :save-text="$t('save')"
                                    >
                                      <div>{{ props.item.to }}</div>
                                      <template v-slot:input>
                                        <div class="mt-4 title">
                                          Update time
                                        </div>
                                      </template>
                                      <template v-slot:input>
                                        <v-select
                                          menu-props="auto"
                                          :items="getOpeningHoursTimes()"
                                          style="margin-top: 16px"
                                          v-model="props.item.to"
                                          label="20:00"
                                          single-line
                                          filled
                                          rounded
                                          hide-details
                                        ></v-select>
                                      </template>
                                    </v-edit-dialog>
                                  </template>
                                </v-data-table>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn
                            color="primary"
                            text
                            rounded
                            @click="dialog = false"
                            >{{ $t("close") }}</v-btn
                          >
                          <v-btn
                            color="success"
                            text
                            rounded
                            @click="
                              saveRestaurantInfo();
                              dialog = false;
                            "
                            >{{ $t("save") }}</v-btn
                          >
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("defaultOrderMessages") }}
                  </span>
                  <v-btn
                    :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                    color="success"
                    text
                    rounded
                    @click="saveDefaultMessages()"
                  >
                    <span v-if="$vuetify.breakpoint.xs">
                      <v-icon>mdi-check-circle</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("save") }}
                    </span>
                  </v-btn>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="restaurant.defaultMessages.confirmation.subject"
                      :label="$t('confirmationSubject')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="restaurant.defaultMessages.confirmation.message"
                      :label="$t('confirmationMessage')"
                      auto-grow
                      rounded
                      filled
                      :hint="$t('confirmationMessageHint')"
                      persistent-hint
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="restaurant.defaultMessages.rejection.subject"
                      :label="$t('rejectionSubject')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="restaurant.defaultMessages.rejection.message"
                      :label="$t('rejectionMessage')"
                      auto-grow
                      rounded
                      filled
                      hide-details
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("customHeadings") }}
                  </span>
                  <v-btn
                    :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                    color="success"
                    text
                    rounded
                    @click="saveCustomHeadings()"
                  >
                    <span v-if="$vuetify.breakpoint.xs">
                      <v-icon>mdi-check-circle</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("save") }}
                    </span>
                  </v-btn>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="restaurant.customHeadings.dailyMenu.name"
                      :label="$t('dailyMenuName')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="restaurant.customHeadings.dailyMenu.description"
                      :label="$t('dailyMenuDescription')"
                      auto-grow
                      rows="3"
                      rounded
                      filled
                      hide-details
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="restaurant.customHeadings.dishes.name"
                      :label="$t('dishesName')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="restaurant.customHeadings.dishes.description"
                      :label="$t('dishesDescription')"
                      auto-grow
                      rows="3"
                      rounded
                      filled
                      hide-details
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("access") }}
                  </span>
                  <v-btn
                    :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                    color="success"
                    text
                    rounded
                    @click="dialogAccess = true"
                  >
                    <span v-if="$vuetify.breakpoint.xs">
                      <v-icon>mdi-plus-circle</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("addAccess") }}
                    </span>
                  </v-btn>

                  <v-dialog v-model="dialogAccess" max-width="400" persistent>
                    <v-card>
                      <v-card-title class="headline mb-2">{{
                        accessFormTitle
                      }}</v-card-title>
                      <v-card-subtitle v-if="editedAccessIndex === -1">{{
                        $t("addNewAccessDescription")
                      }}</v-card-subtitle>
                      <v-form ref="formAccess" v-model="validAccess">
                        <v-card-text class="pt-0">
                          <v-col cols="12" v-if="editedAccessIndex > -1">
                            <v-text-field
                              v-model="editedAccess.user"
                              :label="$t('userId')"
                              readonly
                              rounded
                              filled
                              hide-details
                            ></v-text-field>
                          </v-col>
                          <v-col
                            cols="12"
                            v-if="
                              editedAccess.userEmail || editedAccessIndex === -1
                            "
                          >
                            <v-text-field
                              v-model="editedAccess.userEmail"
                              :rules="[rules.required, rules.email]"
                              :readonly="editedAccessIndex > -1"
                              :label="`${$t('userEmail')} *`"
                              rounded
                              filled
                              hide-details
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12">
                            <v-autocomplete
                              v-model="editedAccess.permissions"
                              :items="permissions"
                              :label="$t('permissions')"
                              :no-data-text="$t('noData')"
                              filled
                              rounded
                              chips
                              small-chips
                              deletable-chips
                              multiple
                              hide-details
                            ></v-autocomplete>
                          </v-col>
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn
                            color="primary"
                            text
                            rounded
                            @click="dialogAccess = false"
                            >{{ $t("close") }}</v-btn
                          >
                          <v-btn
                            color="success"
                            text
                            rounded
                            @click="saveAccess()"
                            :disabled="!validAccess"
                            :loading="dialogAccessLoading"
                            >{{ $t("save") }}</v-btn
                          >
                        </v-card-actions>
                      </v-form>
                    </v-card>
                  </v-dialog>
                </v-card-title>
                <v-row>
                  <v-col cols="12">
                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>{{ $t("userId") }}</th>
                            <th>{{ $t("userEmail") }}</th>
                            <th>{{ $t("permissions") }}</th>
                            <th>{{ $t("actions") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in restaurant.access" :key="item._id">
                            <td>
                              <v-icon v-if="account._id === item.user" small
                                >mdi-account-circle</v-icon
                              >
                              {{ item.user }}
                            </td>
                            <td>
                              {{
                                account._id === item.user
                                  ? account.email
                                  : item.userEmail
                              }}
                            </td>
                            <td>
                              <v-chip
                                v-for="permission in item.permissions"
                                :key="permission"
                                class="ma-1"
                                small
                                >{{ $t(permission) }}</v-chip
                              >
                            </td>
                            <td>
                              <v-icon
                                small
                                class="mr-2"
                                @click="editAccess(item)"
                                :disabled="account._id === item.user"
                                >mdi-pencil</v-icon
                              >
                              <v-icon
                                small
                                @click="removeAccess(item)"
                                :disabled="account._id === item.user"
                                >mdi-delete</v-icon
                              >
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-container>
                <v-card-title class="headline pl-0">
                  <span
                    :class="
                      $vuetify.breakpoint.xs
                        ? 'black--text ellipsis'
                        : 'black--text'
                    "
                  >
                    {{ $t("contactInfo") }}
                  </span>
                  <v-btn
                    :class="$vuetify.breakpoint.xs ? 'ml-1' : 'ml-2'"
                    color="success"
                    text
                    rounded
                    @click="saveUserInfo()"
                  >
                    <span v-if="$vuetify.breakpoint.xs">
                      <v-icon>mdi-check-circle</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("save") }}
                    </span>
                  </v-btn>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="account.info.firstName"
                      :label="$t('firstName')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="account.info.lastName"
                      :label="$t('lastName')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="account.info.mobile"
                      :label="$t('mobile')"
                      rounded
                      filled
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-skeleton-loader>
    <div id="whatsapp"></div>
    <v-snackbar v-model="accessErrorSnackbar" color="error">
      {{ $t("weDidNotFindAnyUserRegisteredWithTheProvidedEmail") }}
      <v-btn text small rounded @click="accessErrorSnackbar = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import settingsService from "../services/settings.service";

export default {
  data() {
    return {
      test_model: null,
      loading: true,
      connectStripeLoading: false,
      dialog: false,
      portosDialog: false,
      portosToken: null,
      generatePortosTokenLoading: false,
      onlineOrderingValid: true,
      rules: {
        required: (value) => !!value || this.$t("requiredField"),
        url: (v) =>
          /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/.test(
            v
          ) || this.$t("invalidUrl"),
        email: (v) =>
          /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(v) ||
          this.$t("emailMustBeCorrect"),
        commaSeparatedEmails: (v) =>
          /^([\w+-.%]+@[\w-.]+\.[A-Za-z]+)(, ?[\w+-.%]+@[\w-.]+\.[A-Za-z]+)*$/.test(
            v
          ) || this.$t("emailMustBeCorrect"),
        minimalOrderValue: (value) =>
          value >= 0 || this.$t("minimalAmountIs", { amount: 0 }),
      },
      timePickerMenuStatus1: false,
      timePickerMenu1: null,
      timePickerMenuStatus2: false,
      timePickerMenu2: null,
      headers: [
        { text: this.$t("day"), value: "day", sortable: false },
        { text: this.$t("from"), value: "from", sortable: false },
        { text: this.$t("to"), value: "to", sortable: false },
      ],
      defaultOpeningHours: [
        {
          day: "monday",
          from: "14:00",
          to: "20:00",
        },
        {
          day: "tuesday",
          from: "14:00",
          to: "20:00",
        },
        {
          day: "wednesday",
          from: "14:00",
          to: "20:00",
        },
        {
          day: "thursday",
          from: "14:00",
          to: "20:00",
        },
        {
          day: "friday",
          from: "14:00",
          to: "20:00",
        },
        {
          day: "saturday",
          from: "14:00",
          to: "20:00",
        },
        {
          day: "sunday",
          from: "14:00",
          to: "20:00",
        },
      ],
      account: {
        _id: null,
        info: {
          firstName: null,
          lastName: null,
          mobile: null,
        },
        email: null,
        currentPassword: null,
        newPassword: null,
      },
      restaurant: {
        isOpen: false,
        automaticIsOpen: false,
        allowPreorderingWhenClosed: false,
        orderScheduling: true,
        dailyMenuOrderingTime: {
          from: null,
          to: null,
        },
        minimalOrderValue: null,
        ordersNotificationEmail: null,
        deliveryOptions: {
          delivery: {
            status: true,
            price: null,
          },
          takeaway: {
            status: true,
          },
          inplace: {
            status: true,
          },
        },
        paymentOptions: {
          cashOnDelivery: {
            status: false,
            allowedTypes: {
              cash: true,
              card: true,
            },
          },
          onlinePayments: {
            status: false,
            returnUrl: "",
          },
        },
        businessDetails: {
          legalName: null,
          businessId: null,
          taxId: null,
          vat: null,
          address: {
            street: null,
            city: null,
            zip: null,
            state: null,
          },
        },
        names: {
          name: null,
          urlName: null,
          voiceName: null,
        },
        info: {
          openingHours: [
            {
              day: "monday",
              from: "14:00",
              to: "20:00",
            },
            {
              day: "tuesday",
              from: "14:00",
              to: "20:00",
            },
            {
              day: "wednesday",
              from: "14:00",
              to: "20:00",
            },
            {
              day: "thursday",
              from: "14:00",
              to: "20:00",
            },
            {
              day: "friday",
              from: "14:00",
              to: "20:00",
            },
            {
              day: "saturday",
              from: "14:00",
              to: "20:00",
            },
            {
              day: "sunday",
              from: "14:00",
              to: "20:00",
            },
          ],
          description: null,
          contact: {
            website: null,
            email: null,
            mobile: null,
            address: {
              street: null,
              city: null,
              zip: null,
              state: null,
            },
          },
        },
        defaultMessages: {
          confirmation: {
            subject: null,
            message: null,
          },
          rejection: {
            subject: null,
            message: null,
          },
        },
        customHeadings: {
          dailyMenu: {
            name: null,
            description: null,
          },
          dishes: {
            name: null,
            description: null,
          },
        },
      },
      permissions: [
        {
          text: this.$t("admin"),
          value: "admin",
        },
        {
          text: this.$t("viewOrders"),
          value: "viewOrders",
        },
        {
          text: this.$t("confirmOrder"),
          value: "confirmOrder",
        },
        {
          text: this.$t("rejectOrder"),
          value: "rejectOrder",
        },
        {
          text: this.$t("completeOrder"),
          value: "completeOrder",
        },
      ],
      dialogAccess: false,
      dialogAccessLoading: false,
      accessErrorSnackbar: false,
      validAccess: true,
      editedAccessIndex: -1,
      editedAccess: {
        userEmail: "",
        permissions: [],
      },
      defaultAccess: {
        userEmail: "",
        permissions: [],
      },
    };
  },
  computed: {
    accessFormTitle() {
      return this.editedAccessIndex === -1
        ? this.$t("addNewAccess")
        : this.$t("editAccess");
    },
  },
  watch: {
    dialogAccess(val) {
      val || this.closeAccess();
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    $("#whatsapp").venomButton({
      phone: "+421950466441",
      avatar:
        "https://cdn.jsdelivr.net/gh/sjasik/piknik.cc@latest/images/whatsapp/avatar.png",
      buttonImage:
        "https://cdn.jsdelivr.net/gh/sjasik/piknik.cc@latest/images/whatsapp/icon.svg",
      nameClient: "Piknik",
      headerTitle: this.$t("usuallyRepliesWithinTheHour"),
      chatMessage: this.$t("whatsappChatMessageSettings"),
      placeholder: this.$t("yourMessage"),
      showPopup: true,
      position: "right",
      size: "64px",
      buttonColor: "#25D366",
    });
    setTimeout(() => {
      $("#whatsapp .venom-button-popup").addClass("active");
    }, 2500);
  },
  methods: {
    async fetchData() {
      const user = await settingsService.getUser();
      this.account = {
        ...this.account,
        ...user,
      };
      const restaurant = await settingsService.getRestaurant(
        localStorage.getItem("restaurantId")
      );

      this.$emit("on-restaurant-remaining-credit-change", restaurant.balance);

      this.restaurant = {
        ...this.restaurant,
        ...restaurant,
        info: { ...this.restaurant.info, ...restaurant.info },
      };
      if (
        !this.restaurant.info.openingHours ||
        this.restaurant.info.openingHours.length === 0
      )
        this.restaurant.info.openingHours = this.defaultOpeningHours;

      this.restaurant.ordersNotificationEmail =
        restaurant.ordersNotificationEmail || restaurant.info.contact.email;

      console.log(this.restaurant);

      this.loading = false;
    },
    async saveUserInfo() {
      this.loading = true;
      const info = this.account.info;
      const resp = await settingsService.editUserInfo(info);
      if (resp) {
        this.account.info = resp;
        this.loading = false;
      }
    },
    async saveOnlineOrdering() {
      this.loading = true;
      const status = this.restaurant.isOpen;
      const automaticIsOpen = this.restaurant.automaticIsOpen;
      const allowPreorderingWhenClosed =
        this.restaurant.allowPreorderingWhenClosed;
      const orderScheduling = this.restaurant.orderScheduling;
      const times = this.restaurant.dailyMenuOrderingTime;
      const minimalOrderValue = this.restaurant.minimalOrderValue;
      const deliveryOptions = this.restaurant.deliveryOptions;
      const paymentOptions = this.restaurant.paymentOptions;
      const ordersNotificationEmail = this.restaurant.ordersNotificationEmail;
      const resp = await settingsService.editOnlineOrdering(
        localStorage.getItem("restaurantId"),
        status,
        automaticIsOpen,
        allowPreorderingWhenClosed,
        orderScheduling,
        times,
        minimalOrderValue,
        deliveryOptions,
        paymentOptions,
        ordersNotificationEmail
      );
      if (resp) {
        this.restaurant.isOpen = resp.isOpen;
        this.restaurant.automaticIsOpen = resp.automaticIsOpen;
        this.restaurant.allowPreorderingWhenClosed =
          resp.allowPreorderingWhenClosed;
        this.restaurant.orderScheduling = resp.orderScheduling;
        this.restaurant.dailyMenuOrderingTime = resp.dailyMenuOrderingTime;
        this.restaurant.minimalOrderValue = resp.minimalOrderValue;
        this.restaurant.deliveryOptions = resp.deliveryOptions;
        this.restaurant.paymentOptions = resp.paymentOptions;
        this.restaurant.ordersNotificationEmail = resp.ordersNotificationEmail;
        this.$emit("on-open-status-change", this.restaurant.isOpen);
        this.$emit(
          "on-allow-preordering-when-closed-change",
          this.restaurant.allowPreorderingWhenClosed
        );
        this.loading = false;
      }
    },
    async connectStripe() {
      this.connectStripeLoading = true;
      const resp = await settingsService.connectStripe(
        localStorage.getItem("restaurantId")
      );
      window.location.replace(resp.url);
    },
    async generatePortosToken() {
      if (window.confirm(this.$t("areYouSureYouWantToGenerateNewApiKey"))) {
        this.generatePortosTokenLoading = true;
        const resp = await settingsService.generatePortosToken(
          localStorage.getItem("restaurantId")
        );
        this.portosToken = resp.portosToken;
        this.restaurant.portosToken = `${this.portosToken.substring(
          0,
          3
        )} . . . ${this.portosToken.slice(-3)}`;
        this.generatePortosTokenLoading = false;
      }
    },
    async saveBusinessDetails() {
      this.loading = true;
      const businessDetails = this.restaurant.businessDetails;
      const resp = await settingsService.editBusinessDetails(
        localStorage.getItem("restaurantId"),
        businessDetails
      );
      if (resp) {
        this.restaurant.businessDetails = resp;
        this.loading = false;
      }
    },
    async saveNames() {
      this.loading = true;
      const names = this.restaurant.names;
      const resp = await settingsService.editNames(
        localStorage.getItem("restaurantId"),
        names
      );
      if (resp) {
        this.restaurant.names = resp;
        this.loading = false;
      }
    },
    getOpeningHoursTimes() {
      const x = 15;
      let times = [];
      let tt = 0;

      for (let i = 0; tt <= 24 * 60; i++) {
        let hh = Math.floor(tt / 60);
        let mm = tt % 60;
        times[i] = ("0" + hh).slice(-2) + ":" + ("0" + mm).slice(-2);
        tt = tt + x;
      }
      return times;
    },
    async saveRestaurantInfo() {
      this.loading = true;
      const info = this.restaurant.info;
      const resp = await settingsService.editRestaurantInfo(
        localStorage.getItem("restaurantId"),
        info
      );
      if (resp) {
        this.restaurant.info = resp;
        this.loading = false;
      }
    },
    async saveDefaultMessages() {
      this.loading = true;
      const defaultMessages = this.restaurant.defaultMessages;
      const resp = await settingsService.editDefaultMessages(
        localStorage.getItem("restaurantId"),
        defaultMessages
      );
      if (resp) {
        this.restaurant.defaultMessages = resp;
        this.loading = false;
      }
    },
    async saveCustomHeadings() {
      this.loading = true;
      const customHeadings = this.restaurant.customHeadings;
      const resp = await settingsService.editCustomHeadings(
        localStorage.getItem("restaurantId"),
        customHeadings
      );
      if (resp) {
        this.restaurant.customHeadings = resp;
        this.loading = false;
      }
    },
    editAccess(access) {
      this.editedAccessIndex = this.restaurant.access.indexOf(access);
      this.editedAccess = Object.assign({}, access);
      this.dialogAccess = true;
    },
    closeAccess() {
      this.dialogAccess = false;
      setTimeout(() => {
        this.editedAccess = Object.assign({}, this.defaultAccess);
        this.editedAccessIndex = -1;
      }, 300);
    },
    async saveAccess() {
      this.dialogAccessLoading = true;
      try {
        if (this.editedAccessIndex > -1) {
          const resp = await settingsService.editAccess(
            localStorage.getItem("restaurantId"),
            this.editedAccess
          );
          this.restaurant.access = resp;
        } else {
          const resp = await settingsService.addAccess(
            localStorage.getItem("restaurantId"),
            this.editedAccess
          );
          this.restaurant.access = resp;
        }
        this.closeAccess();
      } catch (error) {
        if (error.response.status === 400) {
          this.editedAccess.userEmail = "";
          this.accessErrorSnackbar = true;
        }
      }
      this.dialogAccessLoading = false;
    },
    async removeAccess(access) {
      if (window.confirm(this.$t("areYouSureYouWantToDeleteThisAccess"))) {
        this.loading = true;
        const resp = await settingsService.removeAccess(
          localStorage.getItem("restaurantId"),
          access._id
        );
        this.restaurant.access = resp;
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.v-small-dialog__actions .v-btn:nth-child(2) {
  color: #13e46d !important;
}

.v-small-dialog__actions .v-btn {
  border-radius: 28px;
}

.headline .ellipsis {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
